// ===========================================================================
// DEBUG PANEL — never shown unless the page is loaded with ?thrusterDebug.
// Sliders over the three descent engines: where they hang off the ship, the
// shape of the burn, and the colors it cools through. Self-activating,
// js/index.js picks every *Debug.js up on its own. It reads the effects off
// window.app.thrusters, so no production code references it.
//
// "Show descent ship" puts the standing ship on screen with its engines lit,
// from wherever the app is, so the burn can be worked on without playing the
// run to reach it. "Pause game" freezes everything but the engines, which keep
// burning since they are what the sliders are for.
//
// Every slider writes to all three engines at once, and the readout at the
// bottom is the options block to paste back into EDL.initThrusters.
// ===========================================================================

// Where the engines sit in their canvas and how far the burn reaches, all as
// fractions of the canvas, see ThrusterGlow.
const PLACEMENT_PARAMS = [
  ["nozzleX", 0, 1, 0.005],
  ["nozzleY", 0, 1, 0.005],
  ["thrusterLength", 0.05, 0.9, 0.01]
];

// What the burn looks like. These take effect on the next frame, except the
// palette below, which is baked into the sprites.
const LOOK_PARAMS = [
  ["sizeStart", 0, 0.6, 0.01],
  ["sizeEnd", 0, 0.6, 0.01],
  ["spread", 0, 0.2, 0.005],
  ["emitPerSecond", 20, 500, 10],
  ["lifeMin", 0.05, 1.5, 0.01],
  ["lifeMax", 0.05, 2, 0.01],
  ["coreSize", 0, 0.8, 0.01],
  ["coreAlpha", 0, 1, 0.01],
  ["intensity", 0, 1, 0.01]
];

// The panel drives the three engines as one, and reads back off the first.
function getThrusters() {
  return (window.app && window.app.thrusters) || [];
}

function writeParam(key, value) {
  getThrusters().forEach((thruster) => {
    thruster[key] = value;
  });
}

// A color only shows once the sprites are stamped again, they are tinted when
// they are built rather than at draw time.
function writeColor(index, color) {
  getThrusters().forEach((thruster) => {
    thruster.palette = thruster.palette.slice();
    thruster.palette[index] = color;
    thruster.sprites = thruster.buildSprites();
  });
}

// The standing ship with its engines burning, from wherever the app is: screen
// 2, the stage the descent plays on, the sky it plays under, the ship holding
// the last frame of the legs coming down, and all three engines lit. Every
// transition of the run before it is skipped.
function showDescentShip(app) {
  const config = app.game.getSequence().find((entry) => entry.type === "stabilize-ship");

  const launch = () => {
    // Whatever the screen intro was about to start, it is not this.
    app.killTimeline(app.currentTimeline);
    app.game.stop();

    app.fadeSkyToAtmosphere(0.4);
    if (config && config.stage) app.setStage(config.stage, true);

    // The shield is long out by this point of the run and never deploys again.
    app.shieldDeploying = true;
    app.shieldDeployed = Promise.resolve(true);

    app.holdSequenceFrame("descent-landing-legs");
    app.growDescentShip();

    app.lightThrusters(app.thrusters, 0.4);
  };

  if (parseInt(app.currentScreen) === 2) launch();
  else app.goToScreen(2, launch);
}

// The page styles every button with the display font size and leaves its color
// to the browser, which on this panel is black text on a black ground at forty
// odd pixels. So these say everything themselves.
function makeButton(label, onClick) {
  const button = document.createElement("button");

  button.textContent = label;
  button.style.cssText =
    "display:block;width:100%;margin:8px 0 0;padding:6px;border:0;border-radius:4px;" +
    "font:bold 12px/1.4 monospace;color:#000;background:#3EF0FF;cursor:pointer";
  button.addEventListener("click", onClick);

  return button;
}

function buildPanel() {
  const first = getThrusters()[0];

  const panel = document.createElement("div");
  panel.style.cssText =
    "position:fixed;top:12px;right:12px;z-index:99999;background:rgba(0,0,0,.8);" +
    "color:#fff;font:12px/1.4 monospace;padding:12px 14px;border-radius:8px;width:330px;" +
    "max-height:calc(100vh - 24px);overflow:auto";

  const readout = document.createElement("pre");
  readout.style.cssText = "margin:10px 0 0;white-space:pre-wrap;color:#3EF0FF;font-size:11px";

  const refresh = () => {
    const t = getThrusters()[0];
    if (!t) return;

    const colors = t.palette.map((color) => `"${color}"`).join(", ");

    readout.textContent =
      `nozzleX: ${t.nozzleX},\n` +
      `nozzleY: ${t.nozzleY},\n` +
      `thrusterLength: ${t.thrusterLength},\n` +
      `emitPerSecond: ${t.emitPerSecond},\n` +
      `lifeMin: ${t.lifeMin},\n` +
      `lifeMax: ${t.lifeMax},\n` +
      `sizeStart: ${t.sizeStart},\n` +
      `sizeEnd: ${t.sizeEnd},\n` +
      `spread: ${t.spread},\n` +
      `coreSize: ${t.coreSize},\n` +
      `coreAlpha: ${t.coreAlpha},\n` +
      `palette: [${colors}]`;
  };

  const show = makeButton("Show descent ship", () => showDescentShip(window.app));
  const light = makeButton("Light engines", () => window.app.lightThrusters(getThrusters(), 0.4));
  const cut = makeButton("Cut engines", () => window.app.cutThrusters(getThrusters(), 0.4));

  // The engines are left running while everything else is held, otherwise the
  // sliders would have nothing moving to show their effect on. They run on the
  // gsap ticker rather than on a timeline, so the freeze never reaches them.
  const pause = makeButton("Pause game", () => {
    const app = window.app;

    if (app.isGameplayPaused()) {
      app.resumeGameplay();
      pause.textContent = "Pause game";
      return;
    }

    app.pauseGameplay();
    pause.textContent = "Resume game";
  });

  panel.append(show, light, cut, pause);

  const addSliders = (params, title) => {
    const heading = document.createElement("div");
    heading.style.cssText = "margin:10px 0 4px;font-weight:bold;color:#FF3EA5";
    heading.textContent = title;
    panel.append(heading);

    params.forEach(([key, min, max, step]) => {
      const row = document.createElement("label");
      row.style.cssText = "display:flex;align-items:center;gap:8px";

      const name = document.createElement("span");
      name.style.cssText = "flex:0 0 104px;font-size:11px;overflow:hidden;text-overflow:ellipsis";
      name.textContent = key;

      const slider = document.createElement("input");
      slider.type = "range";
      slider.min = min;
      slider.max = max;
      slider.step = step;
      slider.value = first[key];
      slider.style.flex = "1";

      const decimals = step < 0.01 ? 3 : (step < 0.1 ? 2 : (step < 1 ? 1 : 0));

      const val = document.createElement("span");
      val.style.cssText = "flex:0 0 46px;font-size:11px;text-align:right";
      val.textContent = Number(slider.value).toFixed(decimals);

      slider.addEventListener("input", () => {
        const value = parseFloat(slider.value);
        val.textContent = value.toFixed(decimals);
        writeParam(key, value);
        refresh();
      });

      row.append(name, slider, val);
      panel.append(row);
    });
  };

  addSliders(PLACEMENT_PARAMS, "Placement");
  addSliders(LOOK_PARAMS, "Look");

  // Hottest to coolest, the walk a puff makes as it ages. Each one is a color
  // input rather than a slider, the sprites are rebuilt on every change.
  const colorHeading = document.createElement("div");
  colorHeading.style.cssText = "margin:10px 0 4px;font-weight:bold;color:#FF3EA5";
  colorHeading.textContent = "Palette, hottest first";
  panel.append(colorHeading);

  first.palette.forEach((color, index) => {
    const row = document.createElement("label");
    row.style.cssText = "display:flex;align-items:center;gap:8px;margin:2px 0";

    const name = document.createElement("span");
    name.style.cssText = "flex:0 0 104px;font-size:11px";
    name.textContent = `palette ${index}`;

    const input = document.createElement("input");
    input.type = "color";
    input.value = color;
    input.style.cssText = "flex:1;height:22px;background:none;border:0;padding:0";

    const val = document.createElement("span");
    val.style.cssText = "flex:0 0 66px;font-size:11px;text-align:right";
    val.textContent = color;

    input.addEventListener("input", () => {
      const value = input.value.toUpperCase();
      val.textContent = value;
      writeColor(index, value);
      refresh();
    });

    row.append(name, input, val);
    panel.append(row);
  });

  panel.append(readout);
  refresh();

  document.body.appendChild(panel);
}

function init() {
  if (!new URLSearchParams(window.location.search).has("thrusterDebug")) return;

  const tryAttach = () => {
    if (!getThrusters().length) return setTimeout(tryAttach, 200);

    buildPanel();
  };

  tryAttach();
}

init();
